/**
 * Payment API client — centralized fetch wrapper for the MIG payment service.
 * Replaces supabase.functions.invoke() calls for all payment-related endpoints.
 */

import { AI_BASE_URL } from '@/lib/ai-base-url';
import { getAuthToken } from '@/lib/auth-token';

const PAYMENT_BASE_URL = import.meta.env.VITE_PAYMENT_API_URL || AI_BASE_URL;

interface PaymentApiOptions {
  /** Include Firebase JWT auth header. Default: true for authenticated routes. */
  auth?: boolean;
  /** HTTP method. Default: POST */
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
}

/**
 * Call a payment service endpoint.
 *
 * @param path - Route path (e.g., '/pay/create-payment')
 * @param body - Request body (omit for GET requests)
 * @param options - Auth and method options
 * @returns Parsed JSON response
 */
export async function paymentApi<T = any>(
  path: string,
  body?: Record<string, any>,
  options: PaymentApiOptions = {}
): Promise<{ data: T | null; error: string | null; errorData?: Record<string, any> }> {
  const { auth = true, method = 'POST' } = options;

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (auth) {
      const token = await getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    const response = await fetch(`${PAYMENT_BASE_URL}${path}`, {
      method,
      headers,
      ...(body && method !== 'GET' ? { body: JSON.stringify(body) } : {}),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        data: null,
        error: data.error || data.message || `HTTP ${response.status}`,
        errorData: data,
      };
    }

    return { data, error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Network error' };
  }
}
